import { Button } from "./Button";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import { cx } from "../utils/format";

export function Card({ title, tools, children, className }: { title?: string; tools?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("ob-card", className)}>
      {title && <div className="ob-card__head"><h2 className="ob-card__title">{title}</h2>{tools && <span className="ob-card__tools">{tools}</span>}</div>}
      {children}
    </section>
  );
}

export function KpiTile({ value, unit, label, sub, accent }: { value: string; /** quiet word beside the number: "tickets", "this week" */ unit?: string; label: string; sub?: string; accent?: boolean }) {
  return (
    <div className={cx("ob-kpi", accent && "ob-kpi--accent")}>
      <div className="ob-kpi__value">{value}{unit && <span className="ob-kpi__unit">{unit}</span>}</div>
      <div className="ob-kpi__label">{label}</div>
      {sub && <div className="ob-kpi__sub">{sub}</div>}
    </div>
  );
}

export function EmptyState({ icon = "inbox", title, text }: { icon?: IconName; title: string; text?: string }) {
  return (
    <div className="ob-empty">
      <div>
        <div className="ob-empty__icon"><Icon name={icon} /></div>
        <div className="ob-empty__title">{title}</div>
        {text && <div className="ob-empty__text">{text}</div>}
      </div>
    </div>
  );
}

export interface TabItem { id: string; label: string; /** quiet count after the label */ count?: number | string; }
/** Segmented by default (sidebar); `variant="line"` is the underlined header tab (chat, customer profile). The underline is the text colour, not the accent. */
export function Tabs({ items, active, onChange, variant }: { items: TabItem[]; active: string; onChange: (id: string) => void; variant?: "line" | "band" }) {
  return (
    <div className={cx("ob-tabs", variant && `ob-tabs--${variant}`)} role="tablist">
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={t.id === active} className={cx("ob-tabs__item", t.id === active && "ob-tabs__item--active")} onClick={() => onChange(t.id)}>
          {t.label}{t.count != null && t.count !== 0 && <span className="ob-tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** One of a few, all visible at once: the segmented look of `Tabs`, but a choice (a radio group), not a change of view. Icons go before the words.
 *  An item may be `disabled` (shown, not choosable: a provider that is "Coming", 2026-10-19). */
export function Segmented<T extends string>({ label, items, value, onChange }: { label: string; items: { id: T; label: string; icon?: IconName; disabled?: boolean }[]; value: T; onChange: (id: T) => void }) {
  return (
    <div className="ob-tabs ob-tabs--choice" role="radiogroup" aria-label={label}>
      {items.map((t) => (
        <button key={t.id} type="button" role="radio" aria-checked={t.id === value} disabled={t.disabled} className={cx("ob-tabs__item", t.id === value && "ob-tabs__item--active")} onClick={() => onChange(t.id)}>
          {t.icon && <Icon name={t.icon} size="sm" />}{t.label}
        </button>
      ))}
    </div>
  );
}

/** The text flows as one block (inline links stay inline); a button goes in `action`, at the far end. */
/** `stacked` (the first setup round, 2026-10-22): a long text with several actions reads text first, full width, the actions in a row below;
 *  `onClose` adds the close control at the end (a person's own dismissal; the screen keeps where) */
export function Notice({ icon = "alarm", children, action, layout, onClose, strip }: { icon?: IconName; children: ReactNode; action?: ReactNode; layout?: "row" | "stacked"; onClose?: () => void; /** the customer tags and ticket top round: a page's own strip at the top of its column, with the column's gap below ("Deleted on …") */ strip?: boolean }) {
  const close = onClose && <button type="button" className="ob-notice__close" aria-label="Close" onClick={onClose}><Icon name="x" size="sm" /></button>;
  if (layout === "stacked") return <div className={cx("ob-notice ob-notice--stacked", strip && "ob-notice--strip")}><Icon name={icon} /><div className="ob-notice__body"><span className="ob-notice__text">{children}</span>{action && <div className="ob-notice__actions">{action}</div>}</div>{close}</div>;
  return <div className={cx("ob-notice", strip && "ob-notice--strip")}><Icon name={icon} /><span className="ob-notice__text">{children}</span>{action}{close}</div>;
}

/** The kit's paging (the first setup round, 2026-10-22: the back office's Workspaces tab, 50 a page): Previous, where the reader is, Next.
 *  The screen keeps the page in its address and moves it in `onPage`. */
export function Paging({ page, pageSize, total, onPage, noun = "rows" }: { page: number; pageSize: number; total: number; onPage: (page: number) => void; noun?: string }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1, to = Math.min(total, page * pageSize);
  return (
    <nav className="ob-paging" aria-label="Pages">
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
      <span className="ob-paging__where">{from}–{to} of {total} {noun} · page {page} of {pages}</span>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</Button>
    </nav>
  );
}
